import {recordRumError} from '../../../util/datadog/datadogRum';
import {type Trigger} from './TriggersTable/utilTypes';

export type TriggerConfigKind =
  | 'run_state'
  | 'run_metric_threshold'
  | 'run_metric_change'
  | 'run_metric_zscore'
  | 'weave_metric_threshold'
  | 'sandbox_state';

// Why a saved filter could not be read. These are fixed values: error messages
// are never forwarded, because JSON.parse messages can quote the filter.
export type TriggerConfigParseReason =
  | 'empty_filter'
  | 'invalid_json'
  | 'unrecognized_shape'
  | 'unexpected_error';

const toReason = (cause: unknown): TriggerConfigParseReason => {
  if (cause === undefined) {
    return 'unrecognized_shape';
  }
  if (cause === 'empty_filter') {
    return 'empty_filter';
  }
  if (cause instanceof SyntaxError) {
    return 'invalid_json';
  }
  return 'unexpected_error';
};

// Reports a saved automation filter that the UI cannot read back into its
// editor config. The error message is stable per kind so RUM can group and
// alert on it; the trigger ID and event type identify the automation without
// sending the filter itself.
//
// cause is the caught error, 'empty_filter', or omitted when the filter parsed
// but did not have the expected shape.
export const reportTriggerConfigParseError = (
  kind: TriggerConfigKind,
  trigger: Trigger,
  cause?: unknown
): void => {
  const reason = toReason(cause);
  recordRumError(new Error(`Failed to parse ${kind} automation config`), {
    source: 'automations.config_parse',
    config_kind: kind,
    reason,
    trigger_id: trigger.id,
    event_type: trigger.triggeringCondition.eventType,
    // The error class (TypeError, RangeError, ...) carries no filter content.
    ...(reason === 'unexpected_error' &&
      cause instanceof Error && {error_name: cause.name}),
  });
};
